import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { UserDialog } from "@/components/UserDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { USERS } from "@/constants/testIds";
import { ROLE_LABEL } from "@/lib/roles";

const BADGE = { superadmin: "bg-amber-100 text-amber-800", admin: "bg-slate-900 text-white", cs: "bg-sky-50 text-sky-700" };

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState({ open: false, user: null });
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setItems(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (u, active) => {
    try {
      await api.put(`/users/${u.id}`, { active });
      toast.success(active ? "Akun diaktifkan" : "Akun dinonaktifkan");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Akun dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  return (
    <div className="max-w-4xl space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
        <div>
          <p className="overline-label">Pengaturan</p>
          <h1 className="font-heading mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Kelola Pengguna</h1>
          <p className="mt-2 text-sm text-slate-500">Buat akun Admin & CS, atur peran, dan nonaktifkan akses.</p>
        </div>
        <Button onClick={() => setDialog({ open: true, user: null })} className="rounded-full bg-brand hover:bg-brand-dark text-white" data-testid={USERS.addButton}>
          <Plus className="h-4 w-4 mr-1" /> Tambah Akun
        </Button>
      </div>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        {items.map((u) => {
          const isSuper = u.role === "superadmin";
          const locked = isSuper && me.role !== "superadmin";
          return (
          <li key={u.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={USERS.row}>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-50 font-semibold text-brand">{u.name[0]?.toUpperCase()}</div>
            <div className="min-w-0 flex-1 basis-[calc(100%-3.5rem)] sm:basis-auto">
              <p className="font-medium text-slate-900 truncate">{u.name} {u.id === me.id && <span className="text-xs text-slate-400">(Anda)</span>}</p>
              <p className="text-xs text-slate-500 truncate">@{u.username}</p>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${BADGE[u.role]}`} data-testid={USERS.roleBadge}>{ROLE_LABEL[u.role]}</span>
            <div className="flex items-center gap-2">
              <Switch checked={u.active} disabled={u.id === me.id || isSuper} onCheckedChange={(v) => toggle(u, v)} data-testid={USERS.activeSwitch} />
              <span className="text-xs text-slate-500 w-14">{u.active ? "Aktif" : "Nonaktif"}</span>
            </div>
            <div className="ml-auto flex gap-1 sm:ml-0">
              {locked ? (
                <span className="flex items-center gap-1 px-2 text-xs text-slate-400" data-testid={USERS.lockedLabel}><Lock className="h-3.5 w-3.5" /> Terkunci</span>
              ) : (
                <button onClick={() => setDialog({ open: true, user: u })} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Edit" data-testid={USERS.editButton}><Pencil className="h-4 w-4" /></button>
              )}
              {u.id !== me.id && !isSuper && (
                <button onClick={() => setToDelete(u)} className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600" aria-label="Hapus" data-testid={USERS.deleteButton}><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          </li>
          );
        })}
      </ul>
      <UserDialog open={dialog.open} user={dialog.user} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={load} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={remove}
        title="Hapus akun?" description={`Akun "${toDelete?.name}" akan dihapus permanen.`} />
    </div>
  );
}
